import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Divider,
} from '@mui/material';

// ─── Shared dark-theme helpers ──────────────────────────────────────────────
const SECTION_CARD = {
  mb: 3,
  p: 3,
  background: "linear-gradient(135deg, #252535 0%, #1a1a28 100%)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 3,
};

const CODE_BLOCK = {
  bgcolor: "rgba(0,0,0,0.35)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 2,
  p: 2,
  fontFamily: "monospace",
  fontSize: 13,
  color: "rgba(255,255,255,0.9)",
  lineHeight: 1.8,
  my: 2,
};

const TH = { color: "rgba(255,255,255,0.5)", fontWeight: 700, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", py: 1, px: 1.5, borderBottom: "1px solid rgba(255,255,255,0.12)" };
const TD = { color: "rgba(255,255,255,0.8)", fontSize: 13, py: 0.75, px: 1.5, borderBottom: "1px solid rgba(255,255,255,0.06)" };

const DataTable = ({ columns, rows }) => (
  <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", my: 2 }}>
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${columns.length}, 1fr)`, bgcolor: "rgba(255,255,255,0.05)" }}>
      {columns.map(c => <Typography key={c} variant="caption" sx={TH}>{c}</Typography>)}
    </Box>
    {rows.map((row, i) => (
      <Box key={i} sx={{ display: "grid", gridTemplateColumns: `repeat(${columns.length}, 1fr)`, bgcolor: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
        {row.map((cell, j) => (
          <Typography key={j} variant="body2" sx={{ ...TD, color: j === 0 ? "#2af598" : TD.color, fontWeight: j === 0 ? 700 : 400, fontFamily: j === 0 ? "monospace" : "inherit" }}>
            {cell}
          </Typography>
        ))}
      </Box>
    ))}
  </Box>
);

const SectionTitle = ({ number, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
    <Box sx={{ px: 1.25, py: 0.25, bgcolor: "rgba(42,245,152,0.15)", border: "1px solid rgba(42,245,152,0.3)", borderRadius: 1 }}>
      <Typography variant="caption" sx={{ color: "#2af598", fontWeight: 800, fontSize: 11 }}>{number}</Typography>
    </Box>
    <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>{title}</Typography>
  </Box>
);

const SubTitle = ({ children }) => (
  <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600, mt: 3, mb: 1 }}>{children}</Typography>
);

const BodyText = ({ children, sx }) => (
  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.7, ...sx }}>{children}</Typography>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1.5 }}>
    {items.map((item, i) => (
      <Box key={i} component="li" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>{item}</Typography>
      </Box>
    ))}
  </Box>
);

const NumberedList = ({ items }) => (
  <Box component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 1.5 }}>
    {items.map((item, i) => (
      <Box key={i} component="li" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>{item}</Typography>
      </Box>
    ))}
  </Box>
);

const Documentation = () => (
  <Box>

    {/* ── 1. Deposit Rules ── */}
    <Box sx={SECTION_CARD}>
      <SectionTitle number="1" title="Deposit Rules" />
      <Divider sx={{ mb: 2.5, borderColor: "rgba(255,255,255,0.08)" }} />

      <SubTitle>Time Rules</SubTitle>
      <BodyText>Deposits are subject to a 21:00 UTC cutoff time:</BodyText>
      <BulletList items={[
        "00:01 – 21:00 UTC → Same-day approval",
        "21:01 – 00:00 UTC → Next-day approval",
      ]} />

      <Alert severity="info" sx={{ my: 2, "& .MuiAlert-message": { color: "rgba(255,255,255,0.85)" } }}>
        <strong>Trading Start:</strong> All deposits start trading on D+1 (the day after approval). We need 24 hours to move funds into the trading strategy.
      </Alert>

      <SubTitle>Examples</SubTitle>

      <BodyText sx={{ fontWeight: 600, color: "rgba(255,255,255,0.85)", mt: 1, mb: 0.5 }}>Example 1 — No transactions</BodyText>
      <BodyText><strong>Scenario:</strong> Client has $10,000 on Oct 1st. No transactions. Fund performance: 31%.</BodyText>
      <Box sx={CODE_BLOCK}>Result: 31% × $10,000 = <span style={{ color: "#2af598" }}>$3,100 gross profit</span></Box>

      <BodyText sx={{ fontWeight: 600, color: "rgba(255,255,255,0.85)", mt: 2, mb: 0.5 }}>Example 2 — Mid-month withdrawals</BodyText>
      <BodyText><strong>Scenario:</strong> $10,000 on Oct 1. Withdraws $5,000 on Oct 11, $4,000 on Oct 21. Performance: 31%.</BodyText>
      <Box sx={CODE_BLOCK}>
        Days  1–10: $10,000 × 10% = $1,000{"\n"}
        Days 11–20:  $5,000 × 10% = $500{"\n"}
        Days 21–31:  $1,000 × 11% = $110{"\n"}
        <span style={{ color: "#2af598" }}>Total gross profit = $1,610</span>
      </Box>

      <BodyText sx={{ fontWeight: 600, color: "rgba(255,255,255,0.85)", mt: 2, mb: 0.5 }}>Example 3 — Mid-month deposits</BodyText>
      <BodyText><strong>Scenario:</strong> $10,000 on Oct 1. Deposits $5,000 on Oct 10 (starts Oct 11), $1,000 on Oct 20 (starts Oct 21). Performance: 31%.</BodyText>
      <Box sx={CODE_BLOCK}>
        Days  1–10: $10,000 × 10% = $1,000{"\n"}
        Days 11–20: $15,000 × 10% = $1,500{"\n"}
        Days 21–31: $16,000 × 11% = $1,760{"\n"}
        <span style={{ color: "#2af598" }}>Total gross profit = $4,260 · Final balance = $20,260</span>
      </Box>
    </Box>

    {/* ── 2. Withdrawal Rules ── */}
    <Box sx={SECTION_CARD}>
      <SectionTitle number="2" title="Withdrawal Rules" />
      <Divider sx={{ mb: 2.5, borderColor: "rgba(255,255,255,0.08)" }} />

      <SubTitle>Trading Stop: D0</SubTitle>
      <BodyText>Withdrawals stop trading immediately (D0) when the request status changes to 1 (approved/processing).</BodyText>

      <SubTitle>Status Flow</SubTitle>
      <DataTable
        columns={["Status", "Name", "Description", "Trading"]}
        rows={[
          ["0", "PENDING", "OTP verification pending", "Active"],
          ["1", "APPROVED", "Approved / processing", "⛔ Stopped (D0)"],
          ["2", "READY", "Ready to pay", "⛔ Stopped"],
          ["4", "REJECTED", "Rejected by admin", "✅ Resumed"],
          ["5", "PAID", "Payment completed", "N/A"],
        ]}
      />

      <SubTitle>Conditional Offers (Large Withdrawals ≥ 80%)</SubTitle>
      <BodyText>When a client requests withdrawal of ≥80% of net balance, admin can send a "conditional offer" with negative performance.</BodyText>

      <Alert severity="warning" sx={{ my: 2, "& .MuiAlert-message": { color: "rgba(255,255,255,0.85)" } }}>
        <strong>Negative Performance:</strong> Applied to the <em>entire account</em>, not just the withdrawal amount. Client sees their reduced balance and must accept or reject within the time window (default: 1 hour).
      </Alert>

      <BodyText sx={{ fontWeight: 600, color: "rgba(255,255,255,0.85)", mb: 0.5 }}>Example: Conditional Offer</BodyText>
      <BodyText>Client has $10,000 and requests withdrawal of $8,000 (80%). Admin sends conditional offer with -10%.</BodyText>
      <Box sx={CODE_BLOCK}>
        Client sees: "Balance → $9,000 ($10,000 − 10%). Continue?"{"\n\n"}
        If accepted  → balance = $9,000 · withdrawal = $8,000 · remaining = $1,000{"\n"}
        If rejected  → no changes · account stays at $10,000{"\n"}
        If no reply  → request automatically cancelled
      </Box>
    </Box>

    {/* ── 3. Performance Calculation ── */}
    <Box sx={SECTION_CARD}>
      <SectionTitle number="3" title="Performance Calculation" />
      <Divider sx={{ mb: 2.5, borderColor: "rgba(255,255,255,0.08)" }} />

      <SubTitle>Daily Weighting Formula</SubTitle>
      <BodyText>Performance is calculated daily using the client's actual trading balance each day:</BodyText>
      <Box sx={CODE_BLOCK}>
        Daily Profit  = Balance<sub>day</sub> × Percentage<sub>day</sub>{"\n"}
        Monthly Profit = Σ (Daily Profit)
      </Box>

      <SubTitle>Commission Application</SubTitle>
      <BulletList items={[
        "Positive performance → 30% commission applied to gross profit",
        "Negative performance → 0% commission (client bears full loss)",
      ]} />
      <Box sx={CODE_BLOCK}>
        If Gross Profit ≥ 0:{"\n"}
        {"  "}Commission  = Gross Profit × 30%{"\n"}
        {"  "}Net Profit  = Gross Profit − Commission{"\n\n"}
        If Gross Profit {"<"} 0:{"\n"}
        {"  "}Commission  = 0{"\n"}
        {"  "}Net Profit  = Gross Profit
      </Box>
    </Box>

    {/* ── 4. Monthly Closing ── */}
    <Box sx={SECTION_CARD}>
      <SectionTitle number="4" title="Monthly Closing" />
      <Divider sx={{ mb: 2.5, borderColor: "rgba(255,255,255,0.08)" }} />

      <SubTitle>Month End Definition</SubTitle>
      <BodyText>The month closes at 24:00 UTC on the last calendar day of each month. The date admin executes "Add Monthly" does not matter — calculation always anchors to the last day of the month.</BodyText>

      <Alert severity="info" sx={{ my: 2, "& .MuiAlert-message": { color: "rgba(255,255,255,0.85)" } }}>
        If admin runs monthly closing on the 3rd of the following month, the calculation still uses the last day of the previous month as closing date.
      </Alert>

      <SubTitle>Execution Process</SubTitle>
      <NumberedList items={[
        "Daily balances are reconstructed for each client",
        "Deposits and withdrawals applied with D+1 / D0 rules",
        "Gross profit calculated using daily-weighted formula",
        "Commission applied (30% if positive, 0% if negative)",
        "Net profit added to client balance",
        "Records created in: ledgers, monthly_closings, monthly_graphs, transection_requests",
        "Client's net_balance updated in Firestore",
      ]} />
    </Box>

    {/* ── 5. Status Codes Reference ── */}
    <Box sx={SECTION_CARD}>
      <SectionTitle number="5" title="Status Codes Reference" />
      <Divider sx={{ mb: 2.5, borderColor: "rgba(255,255,255,0.08)" }} />

      <SubTitle>Transaction Request Status (status)</SubTitle>
      <DataTable
        columns={["Code", "Name", "Description"]}
        rows={[
          ["0", "PROCESSING", "OTP verification pending"],
          ["1", "WAITING", "Approved, awaiting payment or user action"],
          ["2", "ACCEPTED", "Ready to pay (withdrawal) or completed (deposit)"],
          ["3", "CANCELED", "Cancelled by user or system"],
          ["4", "REJECTED", "Rejected by admin"],
          ["5", "PAID", "Payment completed (withdrawal only) / Auto-pay ready-to-pay"],
        ]}
      />

      <SubTitle>Transaction Type (request_type)</SubTitle>
      <DataTable
        columns={["Code", "Name", "Description"]}
        rows={[
          ["1", "DEPOSIT", "Client deposit request"],
          ["2", "WITHDRAWAL", "Client withdrawal request"],
          ["3", "PERFORMANCE", "Monthly performance record"],
        ]}
      />

      <SubTitle>Ledger Transaction Type (transection_type)</SubTitle>
      <DataTable
        columns={["Code", "Name", "Description"]}
        rows={[
          ["0", "CREDIT (Deposit)", "Funds added to account"],
          ["1", "DEBIT (Withdrawal Request)", "Withdrawal requested — not yet processed"],
          ["2", "DEBIT (Withdrawal)", "Funds removed from account"],
          ["3", "PERFORMANCE", "Monthly profit / loss applied"],
        ]}
      />
    </Box>

  </Box>
);

export default Documentation;
